import { Card, CardContent } from '../ui/card';
import { FileText, Clock, CheckCircle, XCircle } from 'lucide-react';
import { QuoteAnalyticsSummary } from '../../types';

interface QuoteSummaryCardsProps {
  summary: QuoteAnalyticsSummary | null;
  loading: boolean;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);

export function QuoteSummaryCards({ summary, loading }: QuoteSummaryCardsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-1" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const cards = [
    {
      title: 'Total',
      count: summary.totalQuotes,
      value: summary.totalValue,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-l-blue-600'
    },
    {
      title: 'Pendentes',
      count: summary.pendingQuotes,
      value: summary.pendingValue,
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-l-amber-600'
    },
    {
      title: 'Aprovados',
      count: summary.approvedQuotes,
      value: summary.approvedValue,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-l-green-600'
    },
    {
      title: 'Rejeitados',
      count: summary.rejectedQuotes,
      value: summary.rejectedValue,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-l-red-600'
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((card) => (
        <Card key={card.title} className={`hover:shadow-md transition-shadow border-l-4 ${card.borderColor}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{card.title}</p>
                <p className="text-3xl font-bold mt-2 mb-1">{card.count.toLocaleString('pt-BR')}</p>
                <p className="text-sm text-muted-foreground">{formatCurrency(card.value)}</p>
              </div>
              <div className={`p-3 rounded-full ${card.bgColor}`}>
                <card.icon className={`h-6 w-6 ${card.color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
